import { useEffect, useRef, useState } from 'react';
import { MapPin, Phone, Mail, Instagram, ArrowRight, Facebook, Check, Loader2 } from 'lucide-react';
import Button from '../ui/Button';
import Section from '../layout/Section';
import siteContent from '../../data/siteContent.json';

const ContactItem = ({ icon: Icon, title, content, href, className = '' }) => (
    <a
        href={href}
        target={href.startsWith('http') ? '_blank' : '_self'}
        rel={href.startsWith('http') ? 'noopener noreferrer' : ''}
        className={`flex items-center gap-3 p-3 rounded-xl border border-white/5 bg-white/5 hover:bg-white/10 hover:border-accent-gold/40 transition-all duration-300 group cursor-pointer ${className}`}
    >
        <div className="w-9 h-9 rounded-full bg-accent-gold/10 flex items-center justify-center text-accent-gold group-hover:bg-accent-gold group-hover:text-white transition-colors duration-300 shrink-0">
            <Icon size={18} strokeWidth={1.5} />
        </div>
        <div className="flex flex-col">
            <span className="font-heading font-semibold text-white/50 text-xs uppercase tracking-wider mb-0.5">{title}</span>
            <p className="text-white text-sm sm:text-base font-medium group-hover:text-accent-gold transition-colors">{content}</p>
        </div>
    </a>
);

const Contact = () => {
    const { contact, general } = siteContent;
    const mapContainerRef = useRef(null);
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const mapScriptLoadedRef = useRef(false);
    const [shouldLoadMap, setShouldLoadMap] = useState(false);
    const [mapReady, setMapReady] = useState(false);

    // Form State
    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: ''
    });
    const [formStatus, setFormStatus] = useState('idle'); // 'idle', 'submitting', 'success', 'error'

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormStatus('submitting');

        // Simulate API call
        setTimeout(() => {
            setFormStatus('success');
            setFormData({ name: '', phone: '', email: '', message: '' });

            // Reset status after 5 seconds to allow new submission if needed
            setTimeout(() => {
                setFormStatus('idle');
            }, 5000);
        }, 1500);
    };

    useEffect(() => {
        if (!mapContainerRef.current || shouldLoadMap) return;
        if (!('IntersectionObserver' in window)) {
            setShouldLoadMap(true);
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.some((entry) => entry.isIntersecting)) {
                    setShouldLoadMap(true);
                    observer.disconnect();
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(mapContainerRef.current);
        return () => observer.disconnect();
    }, [shouldLoadMap]);

    useEffect(() => {
        const coords = general.contact.coordinates;
        const mapId = import.meta.env.VITE_GOOGLE_MAPS_MAP_ID;
        const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

        if (!shouldLoadMap || !coords || !mapRef.current || mapInstanceRef.current) return;

        if (!apiKey || !mapId) {
            return;
        }

        const initMap = () => {
            if (!window.google || mapInstanceRef.current) return;

            const map = new window.google.maps.Map(mapRef.current, {
                center: { lat: coords.lat, lng: coords.lng },
                zoom: 15,
                mapId,
                clickableIcons: false,
                fullscreenControl: false,
                streetViewControl: false,
                gestureHandling: 'greedy'
            });

            const markerContent = document.createElement('div');
            markerContent.className = 'erde-map-marker__badge';
            markerContent.innerHTML = `
                <img src="/assets/logos/ERDE-white-256.png" alt="Erde Mobilya" />
                <div class="erde-map-marker__tooltip">
                    Konuma gitmek için tıkla
                    <span>${general.contact.address}</span>
                </div>
            `;
            markerContent.addEventListener('click', () => {
                window.open(general.contact.mapLink, '_blank', 'noopener,noreferrer');
            });

            // Advanced marker keeps the logo anchored to the map during zoom/pan
            new window.google.maps.marker.AdvancedMarkerElement({
                map,
                position: { lat: coords.lat, lng: coords.lng },
                content: markerContent
            });

            mapInstanceRef.current = map;
            setMapReady(true);
        };

        if (!mapScriptLoadedRef.current) {
            const script = document.createElement('script');
            script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&map_id=${mapId}&libraries=marker&v=weekly`;
            script.async = true;
            script.defer = true;
            script.onload = () => {
                mapScriptLoadedRef.current = true;
                initMap();
            };
            document.head.appendChild(script);
        } else {
            initMap();
        }
    }, [general.contact.address, general.contact.coordinates, general.contact.mapLink, shouldLoadMap]);

    return (
        <Section id="contact" background="dark" className="py-16 md:py-20">
            <div className="container mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">

                    {/* Left Column: Content (5/12) */}
                    <div className="lg:col-span-5 flex flex-col gap-6">
                        <div>
                            <span className="text-accent-gold font-bold tracking-[0.2em] uppercase text-xs mb-3 block">
                                {contact.badge}
                            </span>
                            <h2 className="text-2xl md:text-4xl font-heading font-bold text-white mb-3 leading-tight">
                                {contact.title}
                            </h2>
                            <p className="text-gray-400 leading-relaxed text-sm md:text-base">
                                {contact.description}
                            </p>
                        </div>

                        {/* Info Cards - Compact */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                            <ContactItem
                                icon={Phone}
                                title="Telefon"
                                content={general.contact.phone}
                                href={`tel:${general.contact.phone.replace(/\s+/g, '')}`}
                            />
                            <ContactItem
                                icon={MapPin}
                                title="Atölye"
                                content={general.contact.address}
                                href={general.contact.mapLink}
                                className="sm:col-span-2"
                            />
                            <ContactItem
                                icon={Mail}
                                title="E-Posta"
                                content={general.contact.email}
                                href={`mailto:${general.contact.email}`}
                            />
                        </div>

                        {/* Quick Social Actions */}
                        <div>
                            <h3 className="font-heading text-white text-sm font-semibold mb-2">Hızlı İletişim</h3>
                            <div className="flex items-center gap-3">
                                <a href={general.contact.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="WhatsApp"
                                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/80 hover:text-white hover:bg-[#25D366] hover:border-[#25D366] hover:scale-110 hover:shadow-[0_0_15px_rgba(37,211,102,0.4)] transition-all duration-300 group">
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.008-.57-.008-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                    </svg>
                                </a>
                                <a href={general.contact.instagram.url} target="_blank" rel="noopener noreferrer" aria-label="Instagram"
                                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/80 hover:text-white hover:bg-[#E4405F] hover:border-[#E4405F] hover:scale-110 hover:shadow-[0_0_15px_rgba(228,64,95,0.4)] transition-all duration-300">
                                    <Instagram size={20} strokeWidth={1.5} />
                                </a>
                                <a href={general.contact.facebook} target="_blank" rel="noopener noreferrer" aria-label="Facebook"
                                    className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center text-white/80 hover:text-white hover:bg-[#1877F2] hover:border-[#1877F2] hover:scale-110 hover:shadow-[0_0_15px_rgba(24,119,242,0.4)] transition-all duration-300">
                                    <Facebook size={20} strokeWidth={1.5} />
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Right Column: Form & Map (7/12) */}
                    <div className="lg:col-span-7 flex flex-col gap-6">

                        {/* Map Container - Optimized Height & Styling */}
                        <div
                            ref={mapContainerRef}
                            className="w-full h-[240px] lg:h-[380px] relative rounded-2xl overflow-hidden shadow-2xl border border-white/10 group order-last lg:order-first"
                        >
                            <div className="w-full h-full relative">
                                <div
                                    ref={mapRef}
                                    className="absolute inset-0 w-full h-full"
                                    role="img"
                                    aria-label="Erde Mobilya konumu"
                                />

                                {!mapReady && (
                                    <div className="absolute inset-0 bg-primary-charcoal/80 flex items-center justify-center text-white text-xs tracking-widest uppercase">
                                        Harita yükleniyor
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Expanded Form Area */}
                        <form className="bg-white/5 p-5 lg:p-7 rounded-2xl border border-white/5 backdrop-blur-sm flex flex-col gap-3 shadow-xl relative overflow-hidden" onSubmit={handleSubmit}>
                            {/* Maintenance/Info Overlay */}
                            {formStatus === 'success' && (
                                <div className="absolute inset-0 bg-primary-charcoal/95 backdrop-blur-md z-20 flex flex-col items-center justify-center text-center p-8 animate-in fade-in duration-300">
                                    <div className="w-16 h-16 rounded-full bg-accent-gold/20 flex items-center justify-center text-accent-gold mb-4 border border-accent-gold/30 shadow-[0_0_30px_rgba(212,175,55,0.2)]">
                                        <Phone size={32} strokeWidth={2} />
                                    </div>
                                    <h3 className="text-xl md:text-2xl font-heading font-bold text-white mb-3">Bilgilendirme</h3>
                                    <p className="text-gray-300 text-sm md:text-base leading-relaxed mb-6">
                                        Form sistemimizdeki geliştirme çalışmaları devam etmektedir.<br />
                                        Lütfen aşağıdaki kanallardan bizimle iletişime geçiniz.
                                    </p>

                                    <div className="flex flex-wrap justify-center gap-3">
                                        <a href={`tel:${general.contact.phone.replace(/\s+/g, '')}`} className="px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 border border-white/10 text-white text-xs font-medium transition-colors flex items-center gap-2">
                                            <Phone size={14} />
                                            Telefon
                                        </a>
                                        <a href={general.contact.whatsapp} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full bg-[#25D366]/20 hover:bg-[#25D366]/30 border border-[#25D366]/30 text-[#25D366] text-xs font-medium transition-colors flex items-center gap-2">
                                            <span>WhatsApp</span>
                                        </a>
                                        <a href={general.contact.facebook} target="_blank" rel="noopener noreferrer" className="px-4 py-2 rounded-full bg-[#1877F2]/20 hover:bg-[#1877F2]/30 border border-[#1877F2]/30 text-[#1877F2] text-xs font-medium transition-colors flex items-center gap-2">
                                            <span>Facebook</span>
                                        </a>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <input
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleInputChange}
                                    required
                                placeholder={contact.form.namePlaceholder}
                                className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-accent-gold focus:ring-1 focus:ring-accent-gold transition-all"
                            />
                                <input
                                    type="tel"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleInputChange}
                                    required
                                placeholder={contact.form.phonePlaceholder}
                                className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-accent-gold focus:ring-1 focus:ring-accent-gold transition-all"
                            />
                            </div>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                            placeholder={contact.form.emailPlaceholder}
                            className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-accent-gold focus:ring-1 focus:ring-accent-gold transition-all"
                        />
                            <textarea
                                name="message"
                                value={formData.message}
                                onChange={handleInputChange}
                            rows="3"
                            required
                            placeholder={contact.form.messagePlaceholder}
                            className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white placeholder:text-gray-500 text-sm focus:outline-none focus:border-accent-gold focus:ring-1 focus:ring-accent-gold transition-all resize-none"
                        ></textarea>

                            <Button
                                variant="primary"
                                disabled={formStatus === 'submitting'}
                                className="w-full flex items-center justify-center gap-2 py-3.5 text-sm md:text-base font-semibold tracking-wide hover:scale-[1.02] hover:shadow-[0_0_20px_rgba(212,175,55,0.25)] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                                {formStatus === 'submitting' ? (
                                    <>
                                        <Loader2 size={18} className="animate-spin" />
                                        <span>Gönderiliyor...</span>
                                    </>
                                ) : (
                                    <>
                                        <span>{contact.form.submitButton}</span>
                                        <ArrowRight size={18} />
                                    </>
                                )}
                            </Button>
                            <p className="text-[10px] text-center text-gray-600 mt-1">
                                {contact.form.privacyNote}
                            </p>
                        </form>
                    </div>

                </div>
            </div>
        </Section>
    );
};
export default Contact;
